import React, { useMemo, useState } from 'react';

import classNames from 'classnames';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';

import { Avatar } from '@buildoutinc/blueprint-react/ui/Avatar';
import { Badge } from '@buildoutinc/blueprint-react/ui/Badge';
import { Empty } from '@buildoutinc/blueprint-react/ui/Empty';
import { Input } from '@buildoutinc/blueprint-react/ui/Input';
import { Label } from '@buildoutinc/blueprint-react/ui/Label';
import { Table } from '@buildoutinc/blueprint-react/ui/Table';
import { Tooltip } from '@buildoutinc/blueprint-react/ui/Tooltip';

import { Button } from 'components/ui/button';
import { Select } from 'components/ui/select';
import { getInitials } from 'helpers/string_helpers';

import type { FilterOption, RosterUser, StatusFilter, UserRosterProps } from './types';

// A sentinel rather than a real slug: Company::Role#assign_slug parameterizes to [a-z0-9_], so no
// company role can ever be named into a collision with this option.
const ALL_ROLES = '*';

const STATUS_OPTIONS: FilterOption[] = [
  { value: 'active', label: 'Active' },
  { value: 'deactivated', label: 'Deactivated' },
  { value: 'all', label: 'All' },
];

const matchesSearch = (user: RosterUser, term: string): boolean => (
  [user.name, user.email].some(field => field?.toLowerCase().includes(term))
);

const matchesStatus = (user: RosterUser, status: StatusFilter): boolean => {
  if (status === 'active') return user.active;
  if (status === 'deactivated') return !user.active;

  return true;
};

// Any of the user's roles, not only the first — a user can hold several.
const matchesRole = (user: RosterUser, slug: string): boolean => (
  slug === ALL_ROLES || user.roles.some(role => role.slug === slug)
);

export default function UserRoster({ users, roles, newUserPath, canCreateUsers }: UserRosterProps) {
  const [query, setQuery] = useState('');
  const [role, setRole] = useState<FilterOption>({ value: ALL_ROLES, label: 'All Roles' });
  const [status, setStatus] = useState<FilterOption>(STATUS_OPTIONS[0]);

  const roleOptions: FilterOption[] = useMemo(() => ([
    { value: ALL_ROLES, label: 'All Roles' },
    ...roles.map(({ slug, name }) => ({ value: slug, label: name })),
  ]), [roles]);

  const visibleUsers = useMemo(() => {
    const term = query.trim().toLowerCase();

    return users.filter(user => (
      (!term || matchesSearch(user, term))
        && matchesRole(user, role.value)
        && matchesStatus(user, status.value as StatusFilter)
    ));
  }, [users, query, role, status]);

  const goToUser = (user: RosterUser) => {
    window.location.href = user.editPath;
  };

  return (
    <div>
      <div className="d-flex flex-wrap align-items-end gap-3 mb-5">
        <div className="flex-grow-1">
          <Label className="d-none d-sm-block" htmlFor="roster-search">Search Users</Label>
          <Input
            id="roster-search"
            placeholder="Search by name or email"
            type="text"
            value={query}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setQuery(event.target.value)}
          />
        </div>

        <div style={{ minWidth: '12rem' }}>
          <Label htmlFor="roster-role">Role</Label>
          <Select<FilterOption>
            aria-label="Role"
            getOptionLabel={option => option.label}
            getOptionValue={option => option.value}
            inputId="roster-role"
            options={roleOptions}
            value={role}
            onChange={option => setRole(option ?? roleOptions[0])}
          />
        </div>

        <div style={{ minWidth: '10rem' }}>
          <Label htmlFor="roster-status">Status</Label>
          <Select<FilterOption>
            aria-label="Status"
            getOptionLabel={option => option.label}
            getOptionValue={option => option.value}
            inputId="roster-status"
            options={STATUS_OPTIONS}
            value={status}
            onChange={option => setStatus(option ?? STATUS_OPTIONS[0])}
          />
        </div>

        {canCreateUsers && (
          <Button href={newUserPath} icon={faPlus} variant="primary">
            Create New User
          </Button>
        )}
      </div>

      {visibleUsers.length === 0 ? (
        <Empty>
          <Empty.Title>No users match those filters</Empty.Title>
          <Empty.Content>Try a different search term, role or status.</Empty.Content>
        </Empty>
      ) : (
        <Table className="align-middle table-hover">
          <Table.Header>
            <Table.Row>
              <Table.Head />
              <Table.Head>Name</Table.Head>
              <Table.Head className="d-none d-sm-table-cell">Roles</Table.Head>
              <Table.Head className="d-none d-sm-table-cell">Office</Table.Head>
              <Table.Head className="d-none d-sm-table-cell">Email Address</Table.Head>
              <Table.Head className="d-none d-sm-table-cell">Phone Number</Table.Head>
              <Table.Head className="d-none d-sm-table-cell">Permissions</Table.Head>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {visibleUsers.map(user => (
              // The whole row is the target, as it is on the slim tab this replaces. role and tabIndex
              // keep it reachable without a mouse, since a tr cannot be an anchor.
              <Table.Row
                aria-label={user.name}
                className={classNames('clickable', { 'text-muted opacity-75': !user.active })}
                key={user.id}
                role="link"
                tabIndex={0}
                onClick={() => goToUser(user)}
                onKeyDown={(event: React.KeyboardEvent<HTMLTableRowElement>) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    goToUser(user);
                  }
                }}
              >
                <Table.Cell style={{ width: '32px' }}>
                  <Avatar>
                    {user.photoUrl && <Avatar.Image alt="" src={user.photoUrl} />}
                    <Avatar.Fallback>{getInitials(user.name)}</Avatar.Fallback>
                  </Avatar>
                </Table.Cell>
                <Table.Cell className="text-nowrap">
                  <strong>{user.name}</strong>
                  {!user.active && <Badge className="ms-2" variant="secondary">Deactivated</Badge>}
                  {user.jobTitle && <span className="d-block text-muted fs-small">{user.jobTitle}</span>}
                </Table.Cell>
                <Table.Cell className="d-none d-sm-table-cell">
                  {user.roles.length === 0 ? (
                    <span className="text-muted fs-small">No role</span>
                  ) : (
                    <div className="d-flex flex-wrap gap-1">
                      {user.roles.map(userRole => (
                        <Badge key={userRole.slug} variant="outline">{userRole.name}</Badge>
                      ))}
                    </div>
                  )}
                </Table.Cell>
                <Table.Cell className="d-none d-sm-table-cell">{user.office}</Table.Cell>
                <Table.Cell className="d-none d-sm-table-cell">{user.email}</Table.Cell>
                <Table.Cell className="d-none d-sm-table-cell">{user.phone}</Table.Cell>
                <Table.Cell className="d-none d-sm-table-cell">
                  {user.permissionSummary.map(permission => (
                    <Tooltip key={permission.abbreviation}>
                      <Tooltip.Trigger
                        delay={0}
                        render={<span className="text-muted fs-small me-1">{permission.abbreviation}</span>}
                      />
                      <Tooltip.Content>{permission.label}</Tooltip.Content>
                    </Tooltip>
                  ))}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
    </div>
  );
}
